import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, TooltipProps } from 'recharts';
import type { SalesAttribution } from 'model/salesDashboard';
import styles from './SalesAttributionChart.module.scss';

const SERIES_COLORS: Record<string, string> = {
    memberships: '#4A7B9D',
    grooming: '#5E8677',
    daycare: '#92B4A7',
    boarding: '#BAA997',
    addons: '#C18733',
    products: '#EAB464',
    custom_items: '#7A7A7A',
    evaluation: '#9B6B9E',
    dental: '#D4856A',
    transportation: '#6B8FD4',
    training: '#8FD46B'
};

const FALLBACK_COLORS = [
    '#4A7B9D',
    '#5E8677',
    '#92B4A7',
    '#BAA997',
    '#C18733',
    '#EAB464',
    '#7A7A7A',
    '#9B6B9E',
    '#D4856A',
    '#6B8FD4',
    '#8FD46B'
];

const getSeriesColor = (key: string, index: number): string =>
    SERIES_COLORS[key] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];

const formatYAxisValue = (value: number): string => {
    if (value >= 1000) return `$${value / 1000}k`;
    return `$${value}`;
};

export interface ChartRow {
    period: string;
    [key: string]: number | string;
}

const CustomTooltip: React.FC<TooltipProps<number, string> & { seriesLabels: Record<string, string> }> = ({
    active,
    payload,
    label,
    seriesLabels
}) => {
    if (!active || !payload || !payload.length) return null;

    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

    const dataWithPercentages = payload.map(entry => ({
        ...entry,
        percentage: total > 0 ? Math.round(((entry.value || 0) / total) * 100) : 0
    }));

    return (
        <div className={styles.customTooltip}>
            <div className={styles.tooltipHeader}>{label}</div>
            <div className={styles.tooltipContent}>
                {dataWithPercentages.map((entry, index) => (
                    <div
                        key={index}
                        className={styles.tooltipItem}
                    >
                        <div className={styles.tooltipItemLeft}>
                            <div
                                className={styles.colorDot}
                                style={{ backgroundColor: entry.color }}
                            />
                            <span className={styles.serviceName}>
                                {seriesLabels[entry.dataKey as string] ?? entry.dataKey}
                            </span>
                        </div>
                        <div className={styles.tooltipItemRight}>
                            <span className={styles.value}>${entry.value?.toLocaleString()}</span>
                            <span className={styles.percentage}>({entry.percentage}%)</span>
                        </div>
                    </div>
                ))}
                <div className={styles.tooltipTotal}>
                    <span className={styles.totalLabel}>Total</span>
                    <span className={styles.totalValue}>${total.toLocaleString()} (100%)</span>
                </div>
            </div>
        </div>
    );
};

interface SalesAttributionChartProps {
    salesAttribution: SalesAttribution;
    chartData: ChartRow[];
    seriesLabels: Record<string, string>;
}

export const SalesAttributionChart: React.FC<SalesAttributionChartProps> = ({
    salesAttribution,
    chartData,
    seriesLabels
}) => (
    <div className={styles.chartWrapper}>
        <ResponsiveContainer
            width="100%"
            height="100%"
            debounce={1}
        >
            <LineChart
                data={chartData}
                margin={{ top: 0, right: 15, left: 10, bottom: 0 }}
            >
                <CartesianGrid
                    strokeDasharray="1 1"
                    horizontal={true}
                    vertical={false}
                    stroke="#e0e0e0"
                />
                <XAxis
                    dataKey="period"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666' }}
                />
                <YAxis
                    tickFormatter={formatYAxisValue}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666' }}
                />
                <Tooltip content={<CustomTooltip seriesLabels={seriesLabels} />} />
                {salesAttribution.series.map((series, index) => {
                    const color = getSeriesColor(series.key, index);
                    return (
                        <Line
                            key={series.key}
                            type="monotone"
                            dataKey={series.key}
                            stroke={color}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 6, fill: color, strokeWidth: 0 }}
                        />
                    );
                })}
            </LineChart>
        </ResponsiveContainer>
    </div>
);
